import React, { useEffect, useRef, useState } from "react";
import apiEndpoints from "@store/apiEndpoints";
import useApiResource from "@pages/TicketNew/useApiResource";
import SearchableSelectField from "../ui/SearchableSelectField";
import TextArea from "../ui/TextArea";
import TextField from "../ui/TextField";
import useDebouncedValue from "../useDebouncedValue";

function FieldError({ message }) {
  return message ? <p className="rma-nc-error-text">{message}</p> : null;
}

/*
A structured address. Country is fixed to the brand site's country and not
shown (it is still sent to the API); state -> suburb -> postcode cascade from
the location API. A suburb's only postcode is auto-filled (but still
editable); several become a dropdown.
*/
export default function AddressFields({ value, onChange, protectedFetch, attempted = false }) {
  const [suburbSearch, setSuburbSearch] = useState("");
  const debouncedSuburbSearch = useDebouncedValue(suburbSearch);

  const states = useApiResource(
    protectedFetch,
    apiEndpoints.location.states(value.country),
    null,
  );
  const stateOptions = (states.data ?? []).map((state) => ({
    value: state.code,
    label: state.name,
  }));

  const suburbs = useApiResource(
    protectedFetch,
    value.state ? apiEndpoints.location.suburbs(value.state, "", debouncedSuburbSearch) : null,
    null,
    { skip: value.state === "" },
  );
  const suburbNames = (suburbs.data?.results ?? []).map((suburb) => suburb.name);
  // Server-side search: the current results may no longer contain the chosen
  // suburb, so keep it as an option or the control would show empty.
  if (value.suburb !== "" && !suburbNames.includes(value.suburb)) {
    suburbNames.unshift(value.suburb);
  }
  const suburbOptions = suburbNames.map((suburb) => ({ value: suburb, label: suburb }));

  const hasSuburb = value.suburb !== "" && value.state !== "";
  const postcodes = useApiResource(
    protectedFetch,
    hasSuburb ? apiEndpoints.location.postcodes(value.suburb, value.state) : null,
    null,
    { skip: !hasSuburb },
  );
  const postcodeList =
    hasSuburb && !postcodes.loading
      ? (postcodes.data?.results ?? []).map((postcode) => postcode.code)
      : [];
  // A suburb spanning several postcodes gets a dropdown; otherwise a free-text field.
  const hasPostcodeChoice = postcodeList.length > 1;
  const postcodeOptions = postcodeList.map((postcode) => ({ value: postcode, label: postcode }));

  // When the user picks a suburb with exactly one postcode, fill it in. Only a
  // pick arms this, so remounting (e.g. switching customer/retailer) never
  // overwrites a postcode the user edited. `staleData` is the previous suburb's
  // response, which useApiResource still reports until the new one lands.
  const suburbKey = value.state + "|" + value.suburb;
  const pendingAutofill = useRef(null);
  useEffect(
    function () {
      const pending = pendingAutofill.current;
      if (!pending || pending.key !== suburbKey) return;
      if (postcodes.loading || !postcodes.data || postcodes.data === pending.staleData) return;
      pendingAutofill.current = null;
      const codes = postcodes.data.results ?? [];
      if (codes.length === 1) onChange({ ...value, postcode: codes[0].code });
    },
    [postcodes.loading, postcodes.data, suburbKey, value, onChange],
  );

  const handleSuburbChange = function (suburb) {
    if (suburb === value.suburb) return;
    pendingAutofill.current = suburb
      ? { key: value.state + "|" + suburb, staleData: postcodes.data }
      : null;
    onChange({ ...value, suburb, postcode: "" });
  };

  const requiredError = (field, message) =>
    attempted && field.trim() === "" ? message : undefined;
  const lineOneError = requiredError(value.lineOne, "Address is required");
  const stateError = requiredError(value.state, "State is required");
  const suburbError = requiredError(value.suburb, "Suburb is required");
  const postcodeError = requiredError(value.postcode, "Postcode is required");

  return (
    <div className="rma-nc-grid-2">
      <div>
        <TextArea
          label="Address Line 1:"
          size="md"
          minHeight={66}
          value={value.lineOne}
          onChange={(event) => onChange({ ...value, lineOne: event.target.value })}
          className={lineOneError ? "rma-nc-control--error" : undefined}
        />
        <FieldError message={lineOneError} />
      </div>
      <TextArea
        label="Address Line 2:"
        size="md"
        minHeight={66}
        value={value.lineTwo}
        onChange={(event) => onChange({ ...value, lineTwo: event.target.value })}
      />
      <div>
        <SearchableSelectField
          label="State"
          placeholder="Select..."
          value={value.state}
          options={stateOptions}
          isLoading={states.loading}
          onChange={(state) => onChange({ ...value, state, suburb: "", postcode: "" })}
          className={stateError ? "rma-nc-rs--error" : undefined}
        />
        <FieldError message={stateError} />
      </div>
      <div>
        <SearchableSelectField
          label="Suburb"
          placeholder="Select..."
          value={value.suburb}
          options={suburbOptions}
          isLoading={suburbs.loading}
          isDisabled={value.state === ""}
          onInputChange={(text) => setSuburbSearch(text)}
          filterOption={null}
          onChange={handleSuburbChange}
          className={suburbError ? "rma-nc-rs--error" : undefined}
        />
        <FieldError message={suburbError} />
      </div>
      <div>
        {hasPostcodeChoice ? (
          <SearchableSelectField
            label="Postcode"
            placeholder="Select..."
            value={value.postcode}
            options={postcodeOptions}
            isLoading={postcodes.loading}
            onChange={(postcode) => onChange({ ...value, postcode })}
            className={postcodeError ? "rma-nc-rs--error" : undefined}
          />
        ) : (
          <TextField
            label="Postcode"
            size="md"
            inputMode="numeric"
            value={value.postcode}
            onChange={(event) => onChange({ ...value, postcode: event.target.value })}
            className={postcodeError ? "rma-nc-control--error" : undefined}
          />
        )}
        <FieldError message={postcodeError} />
      </div>
    </div>
  );
}
